import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Switch,
  Tab,
  Tabs,
} from '@nextui-org/react';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  ExternalLink,
  Globe,
  History,
  Image as ImageIcon,
  Play,
  Plus,
  Save,
  Settings,
  Sparkles,
  Trash2,
  Trophy,
  Upload,
  XCircle,
} from 'lucide-react';
import adminProTournamentsService, {
  ProTournamentAdmin,
  ProTournamentSyncRun,
  ProTournamentSyncSettings,
  ProTournamentSyncSource,
} from '../../services/adminProTournamentsService';

const ACCENT = '#3fced1';

type TabKey = 'tournaments' | 'settings' | 'runs';

// ---------- Helpers ----------

const formatDateTime = (value?: number | string | null): string => {
  if (value == null) return '–';
  const ms = typeof value === 'string' ? new Date(value).getTime() : value;
  if (!Number.isFinite(ms) || ms <= 0) return '–';
  try {
    return new Date(ms).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '–';
  }
};

const statusChip = (status?: string | null) => {
  const map: Record<string, { label: string; className: string }> = {
    UPCOMING: { label: 'Bevorstehend', className: 'bg-[#3fced1]/15 text-[#3fced1]' },
    LIVE: { label: 'Live', className: 'bg-red-500/15 text-red-400' },
    COMPLETED: { label: 'Beendet', className: 'bg-zinc-700 text-gray-300' },
    CANCELLED: { label: 'Abgesagt', className: 'bg-orange-500/15 text-orange-400' },
  };
  const entry = (status && map[status]) || { label: status || 'Unbekannt', className: 'bg-zinc-700 text-gray-300' };
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${entry.className}`}>
      {entry.label}
    </span>
  );
};

const FeedbackBanner: React.FC<{
  message: string | null;
  type: 'success' | 'error';
  onClose: () => void;
}> = ({ message, type, onClose }) => {
  if (!message) return null;
  const isSuccess = type === 'success';
  return (
    <div
      className={`mb-4 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
        isSuccess
          ? 'border-[#3fced1]/40 bg-[#3fced1]/10 text-[#3fced1]'
          : 'border-red-500/40 bg-red-500/10 text-red-400'
      }`}
    >
      {isSuccess ? <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> : <XCircle size={18} className="mt-0.5 shrink-0" />}
      <span className="flex-1">{message}</span>
      <button type="button" onClick={onClose} aria-label="Hinweis schließen" className="opacity-70 hover:opacity-100">
        ✕
      </button>
    </div>
  );
};

// =====================================================================
// Tab: Turniere (Liste + Anlegen + Quellen je Turnier + Einzel-Sync)
// =====================================================================

const SourcesEditor: React.FC<{
  tournament: ProTournamentAdmin;
  onError: (msg: string) => void;
}> = ({ tournament, onError }) => {
  const tournamentId = tournament.id || '';
  const [sources, setSources] = useState<ProTournamentSyncSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [newUrl, setNewUrl] = useState('');
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await adminProTournamentsService.listSources(tournamentId);
      setSources(list);
    } catch (err) {
      onError(adminProTournamentsService.getErrorMessage(err, 'Quellen konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, [tournamentId, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    const url = newUrl.trim();
    if (!url) return;
    setAdding(true);
    try {
      const source = await adminProTournamentsService.createSource({
        tournamentInfoId: tournamentId,
        url,
        name: newName.trim() || undefined,
      });
      setSources((prev) => [...prev, source]);
      setNewUrl('');
      setNewName('');
    } catch (err) {
      onError(adminProTournamentsService.getErrorMessage(err, 'Quelle konnte nicht angelegt werden.'));
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (source: ProTournamentSyncSource, enabled: boolean) => {
    if (!source.id) return;
    setSources((prev) => prev.map((s) => (s.id === source.id ? { ...s, enabled } : s)));
    try {
      await adminProTournamentsService.updateSource(source.id, { enabled });
    } catch (err) {
      setSources((prev) => prev.map((s) => (s.id === source.id ? { ...s, enabled: !enabled } : s)));
      onError(adminProTournamentsService.getErrorMessage(err, 'Quelle konnte nicht aktualisiert werden.'));
    }
  };

  const handleDelete = async (source: ProTournamentSyncSource) => {
    if (!source.id) return;
    try {
      await adminProTournamentsService.deleteSource(source.id);
      setSources((prev) => prev.filter((s) => s.id !== source.id));
    } catch (err) {
      onError(adminProTournamentsService.getErrorMessage(err, 'Quelle konnte nicht gelöscht werden.'));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner size="sm" color="primary" />
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-3 rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Quellen für den KI-Sync ({sources.length})
      </p>

      {sources.length === 0 && (
        <p className="text-sm text-gray-500">
          Noch keine Quellen. Füge Webseiten hinzu (offizielle Turnierseite, Wikipedia, News-Seiten …) –
          der KI-Sync liest sie bei jedem Lauf aus und füllt die Turnierdaten.
        </p>
      )}

      {sources.map((source) => (
        <div
          key={source.id}
          className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2"
        >
          <Globe size={15} className="shrink-0 text-gray-500" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{source.name || source.url}</p>
            <p className="truncate text-xs text-gray-500">{source.url}</p>
            {source.lastError && (
              <p className="mt-0.5 truncate text-xs text-red-400">Letzter Fehler: {source.lastError}</p>
            )}
          </div>
          <span className="text-xs text-gray-500">
            {source.lastCheckedAt ? `Geprüft: ${formatDateTime(source.lastCheckedAt)}` : 'Noch nie geprüft'}
          </span>
          <Switch
            size="sm"
            isSelected={source.enabled !== false}
            onValueChange={(v) => handleToggle(source, v)}
            aria-label="Quelle aktiv"
          />
          <Button
            isIconOnly
            size="sm"
            variant="light"
            aria-label="Quelle löschen"
            className="text-gray-500 hover:text-red-400"
            onPress={() => handleDelete(source)}
          >
            <Trash2 size={15} />
          </Button>
        </div>
      ))}

      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          size="sm"
          placeholder="https:// Quelle hinzufügen …"
          value={newUrl}
          onValueChange={setNewUrl}
          classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-700', input: 'text-white' }}
        />
        <Input
          size="sm"
          placeholder="Name (optional)"
          value={newName}
          onValueChange={setNewName}
          className="sm:max-w-[200px]"
          classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-700', input: 'text-white' }}
        />
        <Button
          size="sm"
          isLoading={adding}
          isDisabled={!newUrl.trim()}
          className="bg-[#3fced1] font-semibold text-black"
          startContent={!adding && <Plus size={15} />}
          onPress={handleAdd}
        >
          Hinzufügen
        </Button>
      </div>
    </div>
  );
};

const TournamentsTab: React.FC<{ onRunStarted: () => void }> = ({ onRunStarted }) => {
  const [tournaments, setTournaments] = useState<ProTournamentAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  // Banner-Verwaltung (eigenes Bild hochladen / KI-Banner entfernen)
  const [bannerBusyId, setBannerBusyId] = useState<string | null>(null);
  const [confirmRemoveBannerId, setConfirmRemoveBannerId] = useState<string | null>(null);
  const bannerInputRef = React.useRef<HTMLInputElement>(null);
  const bannerUploadTargetRef = React.useRef<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newTournamentName, setNewTournamentName] = useState('');
  const [newOrganization, setNewOrganization] = useState('PDC');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await adminProTournamentsService.listTournaments();
      setTournaments(list);
    } catch (err) {
      setError(adminProTournamentsService.getErrorMessage(err, 'Turniere konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    const name = newTournamentName.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      const tournament = await adminProTournamentsService.createTournament({
        name,
        organization: newOrganization.trim() || 'PDC',
      });
      setTournaments((prev) => [tournament, ...prev]);
      setCreateOpen(false);
      setNewTournamentName('');
      setExpandedId(tournament.id || null);
      setFeedback(`"${name}" angelegt. Füge jetzt Quellen hinzu – der KI-Sync erledigt den Rest.`);
    } catch (err) {
      setError(adminProTournamentsService.getErrorMessage(err, 'Turnier konnte nicht angelegt werden.'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (tournament: ProTournamentAdmin) => {
    if (!tournament.id) return;
    if (confirmDeleteId !== tournament.id) {
      setConfirmDeleteId(tournament.id);
      return;
    }
    try {
      await adminProTournamentsService.deleteTournament(tournament.id);
      setTournaments((prev) => prev.filter((t) => t.id !== tournament.id));
      setFeedback('Turnier gelöscht.');
    } catch (err) {
      setError(adminProTournamentsService.getErrorMessage(err, 'Turnier konnte nicht gelöscht werden.'));
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const handlePickBanner = (tournament: ProTournamentAdmin) => {
    if (!tournament.id) return;
    bannerUploadTargetRef.current = tournament.id;
    bannerInputRef.current?.click();
  };

  const handleBannerFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const tournamentId = bannerUploadTargetRef.current;
    event.target.value = ''; // gleicher File-Name erneut wählbar
    if (!file || !tournamentId) return;
    if (!file.type.startsWith('image/')) {
      setError('Bitte eine Bilddatei auswählen (PNG/JPG/WebP).');
      return;
    }
    setBannerBusyId(tournamentId);
    setError(null);
    try {
      const url = await adminProTournamentsService.uploadBannerImage(file);
      const updated = await adminProTournamentsService.setTournamentBanner(tournamentId, url);
      setTournaments((prev) => prev.map((t) => (t.id === tournamentId ? { ...t, ...updated } : t)));
      setFeedback('Eigenes Banner gespeichert – es ersetzt das KI-Banner.');
    } catch (err) {
      setError(adminProTournamentsService.getErrorMessage(err, 'Banner-Upload fehlgeschlagen.'));
    } finally {
      setBannerBusyId(null);
    }
  };

  const handleRemoveBanner = async (tournament: ProTournamentAdmin) => {
    if (!tournament.id) return;
    if (confirmRemoveBannerId !== tournament.id) {
      setConfirmRemoveBannerId(tournament.id);
      return;
    }
    setConfirmRemoveBannerId(null);
    setBannerBusyId(tournament.id);
    setError(null);
    try {
      const updated = await adminProTournamentsService.removeTournamentBanner(tournament.id);
      setTournaments((prev) => prev.map((t) => (t.id === tournament.id ? { ...t, ...updated } : t)));
      setFeedback('Banner entfernt. Hinweis: Ist der KI-Sync mit KI-Bannern aktiv, wird beim nächsten Lauf automatisch ein neues generiert – lade direkt ein eigenes hoch, wenn du das nicht willst.');
    } catch (err) {
      setError(adminProTournamentsService.getErrorMessage(err, 'Banner konnte nicht entfernt werden.'));
    } finally {
      setBannerBusyId(null);
    }
  };

  const handleSyncOne = async (tournament: ProTournamentAdmin) => {
    if (!tournament.id) return;
    setSyncingId(tournament.id);
    setError(null);
    try {
      await adminProTournamentsService.triggerRun(tournament.id);
      setFeedback(`KI-Sync für "${tournament.name}" gestartet – Ergebnis im Tab "Läufe".`);
      onRunStarted();
    } catch (err) {
      setError(adminProTournamentsService.getErrorMessage(err, 'Sync konnte nicht gestartet werden.'));
    } finally {
      setSyncingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div>
      <FeedbackBanner message={feedback} type="success" onClose={() => setFeedback(null)} />
      <FeedbackBanner message={error} type="error" onClose={() => setError(null)} />

      {/* Versteckter File-Input für Banner-Uploads */}
      <input
        ref={bannerInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleBannerFileSelected}
      />

      <div className="mb-5 flex items-center justify-between gap-3">
        <p className="text-sm text-gray-400">
          {tournaments.length} Pro-{tournaments.length === 1 ? 'Turnier' : 'Turniere'} – Name anlegen,
          Quellen hinterlegen, die KI pflegt die Daten.
        </p>
        <Button
          className="bg-[#3fced1] font-semibold text-black"
          startContent={<Plus size={16} />}
          onPress={() => setCreateOpen(true)}
        >
          Neues Turnier
        </Button>
      </div>

      <div className="space-y-4">
        {tournaments.map((tournament) => {
          const id = tournament.id || '';
          const expanded = expandedId === id;
          const thumbnailUrl = adminProTournamentsService.buildFileUrl(tournament.thumbnail);
          return (
            <Card key={id} className="border border-zinc-800 bg-zinc-900">
              <CardBody className="p-4 sm:p-5">
                <div className="flex flex-wrap items-center gap-4">
                  {/* Thumbnail mit Banner-Verwaltung (hochladen / entfernen) */}
                  <div className="group relative flex h-16 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
                    {thumbnailUrl ? (
                      <img src={thumbnailUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <ImageIcon size={20} className="text-zinc-700" />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
                      {bannerBusyId === id ? (
                        <Spinner size="sm" color="primary" />
                      ) : (
                        <>
                          <button
                            type="button"
                            title="Eigenes Banner hochladen"
                            aria-label="Eigenes Banner hochladen"
                            onClick={() => handlePickBanner(tournament)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#3fced1] text-black"
                          >
                            <Upload size={14} />
                          </button>
                          {thumbnailUrl && (
                            <button
                              type="button"
                              title="Banner entfernen"
                              aria-label="Banner entfernen"
                              onClick={() => handleRemoveBanner(tournament)}
                              className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                                confirmRemoveBannerId === id ? 'bg-red-500 text-white' : 'bg-zinc-800 text-gray-300'
                              }`}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-bold text-white">{tournament.name}</h3>
                      {statusChip(tournament.status)}
                      {tournament.updatedBy === 'ki-sync' && (
                        <Chip size="sm" variant="flat" className="bg-[#3fced1]/10 text-[#3fced1]" startContent={<Sparkles size={12} />}>
                          KI-gepflegt
                        </Chip>
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-xs text-gray-500">
                      /turniere/{tournament.slug} · {tournament.dateLabel || 'Termin folgt'} ·
                      {' '}Aktualisiert: {formatDateTime(tournament.updatedAt)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {tournament.slug && (
                      <Button
                        as={RouterLink}
                        to={`/turniere/${tournament.slug}`}
                        size="sm"
                        variant="bordered"
                        className="border-zinc-700 text-gray-300"
                        endContent={<ExternalLink size={13} />}
                      >
                        Ansehen
                      </Button>
                    )}
                    <Button
                      size="sm"
                      isLoading={syncingId === id}
                      className="bg-[#3fced1]/15 font-semibold text-[#3fced1]"
                      startContent={syncingId !== id && <Play size={14} />}
                      onPress={() => handleSyncOne(tournament)}
                    >
                      Jetzt syncen
                    </Button>
                    <Button
                      size="sm"
                      variant="bordered"
                      className="border-zinc-700 text-gray-300"
                      endContent={expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      onPress={() => setExpandedId(expanded ? null : id)}
                    >
                      Quellen
                    </Button>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      aria-label="Turnier löschen"
                      className={confirmDeleteId === id ? 'text-red-400' : 'text-gray-500 hover:text-red-400'}
                      onPress={() => handleDelete(tournament)}
                    >
                      <Trash2 size={15} />
                    </Button>
                  </div>
                </div>
                {confirmDeleteId === id && (
                  <p className="mt-2 text-xs text-red-400">
                    Wirklich löschen? Erneut auf den Papierkorb klicken zum Bestätigen.
                  </p>
                )}
                {confirmRemoveBannerId === id && (
                  <p className="mt-2 text-xs text-red-400">
                    Banner wirklich entfernen? Erneut auf den Papierkorb im Bild klicken.
                  </p>
                )}

                {expanded && <SourcesEditor tournament={tournament} onError={setError} />}
              </CardBody>
            </Card>
          );
        })}

        {tournaments.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <Trophy size={40} className="text-zinc-700" />
            <p className="text-gray-500">
              Noch keine Pro-Turniere. Lege das erste an – ein Name genügt!
            </p>
          </div>
        )}
      </div>

      {/* Anlegen-Modal */}
      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} placement="center">
        <ModalContent className="bg-zinc-900 border border-zinc-800">
          <ModalHeader className="text-white">Neues Pro-Turnier</ModalHeader>
          <ModalBody>
            <Input
              autoFocus
              label="Turniername"
              placeholder='z.B. "PDC World Grand Prix"'
              value={newTournamentName}
              onValueChange={setNewTournamentName}
              classNames={{ inputWrapper: 'bg-zinc-800 border border-zinc-700', input: 'text-white', label: 'text-gray-400' }}
            />
            <Input
              label="Verband"
              value={newOrganization}
              onValueChange={setNewOrganization}
              description="z.B. PDC, WDF, WSDT"
              classNames={{ inputWrapper: 'bg-zinc-800 border border-zinc-700', input: 'text-white', label: 'text-gray-400' }}
            />
            <p className="text-xs text-gray-500">
              Mehr braucht es nicht: Danach Quellen hinterlegen – Termine, Venue, Preisgeld,
              Format, Historie und das Banner übernimmt die KI.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setCreateOpen(false)}>Abbrechen</Button>
            <Button
              isLoading={creating}
              isDisabled={!newTournamentName.trim()}
              className="bg-[#3fced1] font-semibold text-black"
              onPress={handleCreate}
            >
              Anlegen
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

// =====================================================================
// Tab: Einstellungen
// =====================================================================

const SettingsTab: React.FC<{ onRunStarted: () => void }> = ({ onRunStarted }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastRunAt, setLastRunAt] = useState<string | number | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState('60');
  const [aiBanners, setAiBanners] = useState(true);
  const [claudeModel, setClaudeModel] = useState('');

  useEffect(() => {
    let cancelled = false;
    adminProTournamentsService
      .getSettings()
      .then((settings: ProTournamentSyncSettings) => {
        if (cancelled) return;
        setEnabled(settings.enabled === true);
        setIntervalMinutes(String(settings.intervalMinutes ?? 60));
        setAiBanners(settings.aiBanners !== false);
        setClaudeModel(settings.claudeModel || '');
        setLastRunAt(settings.lastRunAt ?? null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(adminProTournamentsService.getErrorMessage(err, 'Einstellungen konnten nicht geladen werden.'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    const interval = parseInt(intervalMinutes, 10);
    if (isNaN(interval) || interval < 10 || interval > 1440) {
      setError('Das Intervall muss zwischen 10 und 1440 Minuten liegen.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const settings = await adminProTournamentsService.updateSettings({
        enabled,
        intervalMinutes: interval,
        aiBanners,
        claudeModel: claudeModel.trim(),
      });
      setLastRunAt(settings.lastRunAt ?? lastRunAt);
      setFeedback('Einstellungen wurden gespeichert.');
    } catch (err) {
      setError(adminProTournamentsService.getErrorMessage(err, 'Einstellungen konnten nicht gespeichert werden.'));
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    setError(null);
    try {
      await adminProTournamentsService.triggerRun();
      setFeedback('Sync-Lauf gestartet – Ergebnis im Tab "Läufe".');
      onRunStarted();
    } catch (err) {
      setError(adminProTournamentsService.getErrorMessage(err, 'Lauf konnte nicht gestartet werden.'));
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <FeedbackBanner message={feedback} type="success" onClose={() => setFeedback(null)} />
      <FeedbackBanner message={error} type="error" onClose={() => setError(null)} />

      <Card className="border border-zinc-800 bg-zinc-900">
        <CardBody className="gap-5 p-5 sm:p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-white">KI-Sync aktiv</p>
              <p className="mt-0.5 text-xs text-gray-400">
                Regelmäßige Daten-Syncs im eingestellten Intervall ausführen.
              </p>
            </div>
            <Switch isSelected={enabled} onValueChange={setEnabled} aria-label="KI-Sync aktiv" />
          </div>

          <Input
            label="Intervall (Minuten, 10–1440)"
            type="number"
            min={10}
            max={1440}
            value={intervalMinutes}
            onValueChange={setIntervalMinutes}
            classNames={{ inputWrapper: 'bg-zinc-800 border border-zinc-700', input: 'text-white', label: 'text-gray-400' }}
          />

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-white">KI-Banner</p>
              <p className="mt-0.5 text-xs text-gray-400">
                Fehlt einem Turnier das Thumbnail, entwirft die KI automatisch ein Banner im DartCloud-CI.
              </p>
            </div>
            <Switch isSelected={aiBanners} onValueChange={setAiBanners} aria-label="KI-Banner" />
          </div>

          <Input
            label="Claude-Modell (leer = Standard)"
            placeholder="z.B. claude-fable-5"
            value={claudeModel}
            onValueChange={setClaudeModel}
            classNames={{ inputWrapper: 'bg-zinc-800 border border-zinc-700', input: 'text-white', label: 'text-gray-400' }}
          />

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-800 pt-4">
            <p className="flex items-center gap-1.5 text-xs text-gray-500">
              <Clock size={13} />
              Letzter Lauf: {formatDateTime(lastRunAt)}
            </p>
            <div className="flex gap-2">
              <Button
                variant="bordered"
                className="border-zinc-700 text-gray-300"
                isLoading={running}
                startContent={!running && <Play size={15} />}
                onPress={handleRunNow}
              >
                Jetzt ausführen
              </Button>
              <Button
                className="bg-[#3fced1] font-semibold text-black"
                isLoading={saving}
                startContent={!saving && <Save size={15} />}
                onPress={handleSave}
              >
                Speichern
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

// =====================================================================
// Tab: Läufe
// =====================================================================

const RunsTab: React.FC<{ refreshToken: number }> = ({ refreshToken }) => {
  const [runs, setRuns] = useState<ProTournamentSyncRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await adminProTournamentsService.listRuns(20);
      setRuns(result.runs);
      return result.runs;
    } catch (err) {
      setError(adminProTournamentsService.getErrorMessage(err, 'Läufe konnten nicht geladen werden.'));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  // Auto-Refresh alle 10s, solange ein Lauf läuft
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    const hasRunning = runs.some((r) => r.status === 'running');
    if (hasRunning) {
      pollRef.current = setInterval(load, 10000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [runs, load]);

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div>
      <FeedbackBanner message={error} type="error" onClose={() => setError(null)} />
      <div className="space-y-3">
        {runs.map((run) => (
          <Card key={run.id} className="border border-zinc-800 bg-zinc-900">
            <CardBody className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                {run.status === 'running' ? (
                  <span className="flex h-2.5 w-2.5 animate-pulse rounded-full bg-[#3fced1]" />
                ) : run.status === 'success' ? (
                  <CheckCircle2 size={16} className="text-[#3fced1]" />
                ) : (
                  <AlertTriangle size={16} className="text-orange-400" />
                )}
                <span className="text-sm font-semibold text-white">
                  {run.status === 'running' ? 'Läuft …' : run.status === 'success' ? 'Erfolgreich' : 'Mit Fehlern'}
                </span>
                <span className="text-xs text-gray-500">
                  {formatDateTime(run.startedAt)} · {run.triggeredBy === 'cron' ? 'Automatisch' : 'Manuell'} ·
                  {' '}{run.tournamentsProcessed ?? 0} Turnier(e)
                </span>
              </div>

              {(run.results ?? []).length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {(run.results ?? []).map((result, idx) => (
                    <div key={`${run.id}-${idx}`} className="rounded-lg bg-zinc-950/60 px-3 py-2 text-xs">
                      <span className="font-semibold text-white">{result.name || result.slug}</span>
                      <span className="text-gray-500">
                        {' '}– {result.sourcesUsed ?? 0} Quelle(n)
                        {(result.updatedFields ?? []).length > 0 && (
                          <> · aktualisiert: <span className="text-[#3fced1]">{(result.updatedFields ?? []).join(', ')}</span></>
                        )}
                        {result.bannerGenerated && <> · <span className="text-[#3fced1]">Banner generiert</span></>}
                        {(result.updatedFields ?? []).length === 0 && !result.bannerGenerated && !result.error && ' · keine Änderungen'}
                      </span>
                      {result.error && <p className="mt-1 text-red-400">{result.error}</p>}
                    </div>
                  ))}
                </div>
              )}

              {(run.errors ?? []).length > 0 && (
                <div className="mt-2 space-y-1">
                  {(run.errors ?? []).map((err, idx) => (
                    <p key={`${run.id}-err-${idx}`} className="text-xs text-orange-400">{err}</p>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        ))}

        {runs.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <History size={40} className="text-zinc-700" />
            <p className="text-gray-500">Noch keine Sync-Läufe.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// =====================================================================
// Seite
// =====================================================================

const AdminProTournamentsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('tournaments');
  const [runsRefreshToken, setRunsRefreshToken] = useState(0);

  const handleRunStarted = () => setRunsRefreshToken((t) => t + 1);

  return (
    <>
      <Helmet>
        <title>Pro-Turniere | Admin | DartCloud</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex items-center gap-3">
          <Button
            as={RouterLink}
            to="/admin"
            isIconOnly
            variant="bordered"
            className="border-zinc-700 text-gray-300"
            aria-label="Zurück zum Admin-Dashboard"
          >
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-extrabold tracking-tight text-white">
              <Trophy size={22} style={{ color: ACCENT }} />
              Pro-Turniere
            </h1>
            <p className="text-sm text-gray-400">
              Turnier anlegen, Quellen hinterlegen – Claude pflegt Daten & Banner automatisch.
            </p>
          </div>
        </div>

        <Tabs
          selectedKey={activeTab}
          onSelectionChange={(key) => setActiveTab(key as TabKey)}
          variant="underlined"
          classNames={{
            tabList: 'gap-6 border-b border-zinc-800 w-full',
            cursor: 'bg-[#3fced1]',
            tab: 'text-gray-400 data-[selected=true]:text-white',
          }}
        >
          <Tab
            key="tournaments"
            title={(
              <span className="flex items-center gap-1.5">
                <Trophy size={15} /> Turniere
              </span>
            )}
          >
            <div className="pt-5">
              <TournamentsTab onRunStarted={handleRunStarted} />
            </div>
          </Tab>
          <Tab
            key="settings"
            title={(
              <span className="flex items-center gap-1.5">
                <Settings size={15} /> Einstellungen
              </span>
            )}
          >
            <div className="pt-5">
              <SettingsTab onRunStarted={handleRunStarted} />
            </div>
          </Tab>
          <Tab
            key="runs"
            title={(
              <span className="flex items-center gap-1.5">
                <History size={15} /> Läufe
              </span>
            )}
          >
            <div className="pt-5">
              <RunsTab refreshToken={runsRefreshToken} />
            </div>
          </Tab>
        </Tabs>
      </div>
    </>
  );
};

export default AdminProTournamentsPage;
